import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'

import type { LogStreamLevelType, OperatingModeType, PointOfSale } from '@/api/admin-sales-channels-types'
import { OperatingMode } from '@/api/admin-sales-channels-types'
import { useUpdatePointOfSale } from '@/api/api-mutations'
import {
	preloadListPointOfSaleGroups,
	preloadListSalesChannels,
	useListPointOfSaleGroups,
	useListSalesChannels
} from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, FormHint, FormLabel, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker } from '@/components/route-modal'
import { useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

export const Route = createFileRoute('/_authenticated/settings/points-of-sale/$pointOfSaleUuid/edit')({
	component: PointOfSaleEdit,
	loader: async ({ context }) => {
		await preloadListSalesChannels(context, {})
		await preloadListPointOfSaleGroups(context, {})
	}
})

// `GetPointOfSale`/`UpdatePointOfSale` expose config fields through an untyped `metadata: any` —
// read log streaming fields off it rather than plumbing a typed config.
type PointOfSaleEditMetadata = {
	logStreamLevel?: LogStreamLevelType
	logStreamId?: string
	kioskPin?: string
	safeAreaInsets?: { top?: number | null; right?: number | null; bottom?: number | null; left?: number | null }
}

const LOG_STREAM_LEVEL_VALUES: LogStreamLevelType[] = ['OFF', 'ERROR', 'WARN', 'INFO', 'DEBUG']
const OPERATING_MODE_VALUES = Object.values(OperatingMode) as OperatingModeType[]

// CloudWatch Logs stream names may not exceed 512 chars or contain ':'/'*'; the server prefixes
// this value with `${tenantId}/`, so keep some headroom rather than validating the exact API limit.
const MAX_LOG_STREAM_ID_LENGTH = 400

function PointOfSaleEdit() {
	const { pointOfSaleUuid } = Route.useParams()

	return (
		<RouteModal
			prev='..'
			asDrawer
			requiresData
			loader={async api => await api.getPointOfSale({ path: { pointOfSaleUuid } })}
		>
			{data => <PointOfSaleEditComponent pointOfSale={data} />}
		</RouteModal>
	)
}

function PointOfSaleEditComponent({ pointOfSale }: { pointOfSale: PointOfSale }) {
	const { L } = useI18n()
	const { pointOfSaleUuid } = Route.useParams()
	const { handleSuccess } = useRouteModal()

	const updatePointOfSale = useUpdatePointOfSale()
	const salesChannels = useListSalesChannels()

	const salesChannelOptions = useMemo(
		() =>
			salesChannels.data?.map(channel => ({
				label: channel.name,
				value: channel.uuid
			})) || [],
		[salesChannels.data]
	)

	const logStreamLevelOptions = useMemo(
		() => LOG_STREAM_LEVEL_VALUES.map(value => ({ label: L.Settings.PointOfSale.LogStreamLevel(value), value })),
		[L]
	)

	const operatingModeOptions = useMemo(
		() => OPERATING_MODE_VALUES.map(value => ({ label: L.Settings.PointOfSale.OperatingMode(value), value })),
		[L]
	)

	const pointOfSaleGroups = useListPointOfSaleGroups()
	const groupOptions = useMemo(
		() => [
			{ value: '', label: L.Settings.PointOfSale.GroupNone() },
			...(pointOfSaleGroups.data ?? []).map(group => ({ value: group.uuid, label: group.name }))
		],
		[L, pointOfSaleGroups.data]
	)

	const metadata = pointOfSale.metadata as PointOfSaleEditMetadata | undefined

	const form = useAppForm({
		defaultValues: {
			name: pointOfSale.name,
			identifier: pointOfSale.identifier,
			salesChannelUuids: pointOfSale.salesChannels.map(sc => sc.uuid),
			pointOfSaleGroupUuid: pointOfSale.group?.uuid ?? '',
			allowedOperatingModes: pointOfSale.allowedOperatingModes,
			logStreamLevel: metadata?.logStreamLevel ?? 'WARN',
			logStreamId: metadata?.logStreamId ?? '',
			kioskPin: metadata?.kioskPin ?? '',
			safeAreaInsetTop: metadata?.safeAreaInsets?.top ?? undefined,
			safeAreaInsetRight: metadata?.safeAreaInsets?.right ?? undefined,
			safeAreaInsetBottom: metadata?.safeAreaInsets?.bottom ?? undefined,
			safeAreaInsetLeft: metadata?.safeAreaInsets?.left ?? undefined
		},
		onSubmit: async ({ value }) => {
			const safeAreaInsets = {
				top: value.safeAreaInsetTop,
				right: value.safeAreaInsetRight,
				bottom: value.safeAreaInsetBottom,
				left: value.safeAreaInsetLeft
			}
			const result = await updatePointOfSale({
				path: { pointOfSaleUuid },
				body: {
					name: value.name,
					identifier: value.identifier,
					salesChannelUuids: value.salesChannelUuids,
					pointOfSaleGroupUuid: value.pointOfSaleGroupUuid,
					allowedOperatingModes: value.allowedOperatingModes,
					logStreamLevel: value.logStreamLevel,
					// Explicit `null` (rather than `undefined`) so clearing the field actually
					// removes the override server-side instead of being treated as "unchanged".
					logStreamId: value.logStreamId.trim() || null,
					kioskPin: value.kioskPin.trim() || null,
					// `null` when every side is blank, so clearing them all actually removes the
					// override server-side instead of persisting an all-`undefined` object.
					safeAreaInsets: Object.values(safeAreaInsets).some(v => v !== undefined) ? safeAreaInsets : null
				}
			})
			if (result.isSuccess) {
				toast.success(L.Settings.PointOfSale.EditSuccess())
				handleSuccess()
			} else {
				toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
			}
		}
	})

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Settings.PointOfSale.EditTitle()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField name='name' children={field => <field.TextField label={L.Fields.Name()} />} />
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField name='identifier' children={field => <field.TextField label='Identifier' />} />
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='salesChannelUuids'
							children={field => (
								<field.ComboboxField
									label={L.Fields.SalesChannel()}
									placeholder='Select sales channels'
									multiple
									options={salesChannelOptions}
									optionsLoading={salesChannels.isLoading}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='pointOfSaleGroupUuid'
							children={field => (
								<field.SelectField
									label={L.Settings.PointOfSale.Column.Group()}
									options={groupOptions}
									optionsLoading={pointOfSaleGroups.isLoading}
								/>
							)}
						/>
						<Text size='small' className='text-ui-fg-muted'>
							{L.Settings.PointOfSale.GroupOptionalHint()}
						</Text>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='allowedOperatingModes'
							children={field => (
								<field.ComboboxField
									label={L.Settings.PointOfSale.AllowedOperatingModesLabel()}
									placeholder={L.Settings.PointOfSale.SelectOperatingModes()}
									multiple
									options={operatingModeOptions}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='logStreamLevel'
							children={field => (
								<field.SelectField
									label={L.Settings.PointOfSale.LogStreamLevelLabel()}
									options={logStreamLevelOptions}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='logStreamId'
							validators={{
								onChange: ({ value }) => {
									if (!value) {
										return undefined
									}
									if (/[:*]/.test(value)) {
										return L.Settings.PointOfSale.LogStreamIdInvalidChars()
									}
									if (value.length > MAX_LOG_STREAM_ID_LENGTH) {
										return L.Settings.PointOfSale.LogStreamIdTooLong()
									}
									return undefined
								}
							}}
							children={field => (
								<field.TextField
									label={L.Settings.PointOfSale.LogStreamIdLabel()}
									optional
									placeholder={L.Settings.PointOfSale.LogStreamIdPlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<form.AppField
							name='kioskPin'
							validators={{
								onChange: ({ value }) => {
									if (!value) {
										return undefined
									}
									return /^\d{6}$/.test(value) ? undefined : L.Settings.PointOfSale.KioskPinInvalid()
								}
							}}
							children={field => (
								<field.TextField
									label={L.Settings.PointOfSale.KioskPinLabel()}
									optional
									placeholder={L.Settings.PointOfSale.KioskPinPlaceholder()}
								/>
							)}
						/>
					</FormFieldGroup>

					<FormFieldGroup>
						<FormLabel optional>{L.Settings.PointOfSale.SafeAreaInsetsLabel()}</FormLabel>
						<FormHint>{L.Settings.PointOfSale.SafeAreaInsetsHint()}</FormHint>
						<div className='grid grid-cols-2 gap-4 sm:grid-cols-4'>
							<form.AppField
								name='safeAreaInsetTop'
								children={field => (
									<field.NumberField label={L.Settings.PointOfSale.SafeAreaInsetTop()} min={0} />
								)}
							/>
							<form.AppField
								name='safeAreaInsetRight'
								children={field => (
									<field.NumberField label={L.Settings.PointOfSale.SafeAreaInsetRight()} min={0} />
								)}
							/>
							<form.AppField
								name='safeAreaInsetBottom'
								children={field => (
									<field.NumberField label={L.Settings.PointOfSale.SafeAreaInsetBottom()} min={0} />
								)}
							/>
							<form.AppField
								name='safeAreaInsetLeft'
								children={field => (
									<field.NumberField label={L.Settings.PointOfSale.SafeAreaInsetLeft()} min={0} />
								)}
							/>
						</div>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.SaveButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
